/**
 * Small helpers around Privy's session — kept apart from `lib/api.ts`
 * (which targets anonymous/voice endpoints) so the auth surface stays
 * easy to grep for and easy to mock.
 */
const BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? 'http://localhost:3001';

export interface MeUser {
  id: string;
  privyDid: string;
  email: string | null;
  displayName: string | null;
}

/**
 * Idempotent server-side sync: trades a Privy token (identity token
 * preferred, access token also accepted) for a local user row. The
 * backend's `PrivyService.getUser` tries the identity-token path first
 * and silently falls back to `getUserById(did)` if the caller sent the
 * access token. Call once right after Privy reports `authenticated=true`.
 * Re-running picks up newly linked accounts without creating duplicates.
 */
export async function syncMe(privyToken: string): Promise<MeUser> {
  const res = await fetch(`${BASE}/api/users/me`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${privyToken}` },
  });
  if (!res.ok) throw new Error(`users/me sync failed: ${res.status}`);
  const body = (await res.json()) as { user: MeUser };
  return body.user;
}

/**
 * Read-only fetch of the local user. Returns null for anonymous callers
 * (200 + null body — the backend never 401s GET /api/users/me so we
 * can render branchlessly).
 */
export async function fetchMe(privyAccessToken: string | null): Promise<MeUser | null> {
  const headers: Record<string, string> = {};
  if (privyAccessToken) headers.Authorization = `Bearer ${privyAccessToken}`;
  const res = await fetch(`${BASE}/api/users/me`, { headers });
  if (!res.ok) return null;
  const body = (await res.json()) as { user: MeUser | null };
  return body.user;
}
